import { useState, useRef, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { auth } from '@tabletuck/firebase-services';
import { environment } from 'apps/table-tuck-web/src/environments/environment';
import { fetchData } from '../Utils/UtilsFunctions';

/**
 * Shared hook for calculating backend pricing for the current cart.
 *
 * Responsibilities:
 * - Calls /calculateSquareItemsPrice with the current cart/store/order type
 * - Manages loading and calculating states
 * - Debounces and throttles API calls for performance
 * - Caches auth token to avoid repeated getIdToken calls
 * - Ignores stale responses using a request id guard
 *
 * NOTE: This hook does not own any UI concerns. Components decide
 *       when it should be enabled and what to do with the data.
 */
export const useCartPricing = ({
  cart,
  cartStoreId,
  orderType,
  deliveryAddressId,
  enabled,
}) => {
  const [apiPricingData, setApiPricingData] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [pricingError, setPricingError] = useState(null);

  // Debounce timer for API calls
  const apiCallTimeoutRef = useRef(null);
  // Cache for auth token to avoid repeated getIdToken calls
  const authTokenCacheRef = useRef(null);
  const tokenTimestampRef = useRef(0);
  const TOKEN_CACHE_DURATION = 5000; // Cache for 5 seconds

  // Track last cart to detect changes more reliably
  // Use JSON.stringify for deep comparison since cart array reference might not change
  const lastCartStringRef = useRef(null);

  // Guard to prevent multiple simultaneous API calls
  const isApiCallInProgressRef = useRef(false);

  // Throttle guard to avoid firing API calls too frequently
  const lastApiCallRef = useRef(0);
  const API_CALL_THROTTLE = 300; // Minimum 300ms between API calls

  // Request ID to ignore stale responses from outdated requests
  const currentRequestIdRef = useRef(0);
  // Track if we've loaded initial data (to distinguish initial load from updates)
  const hasInitialDataRef = useRef(false);

  // Delivery address resolver used by the API call
  const getCurrentDeliveryAddressId = useCallback(() => {
    if (orderType === 'delivery') {
      return deliveryAddressId || '';
    }
    return '';
  }, [orderType, deliveryAddressId]);

  // Core API call (no debouncing). Can be used for "immediate" pricing refresh.
  const callCalculateSquareItemsPriceImmediate = useCallback(
    async (currentType = null, explicitDeliveryId = null) => {
      if (!enabled) {
        return;
      }

      // Only call API if user is authenticated
      if (!auth?.currentUser?.uid) {
        return;
      }

      // Prevent multiple simultaneous API calls
      if (isApiCallInProgressRef.current) {
        console.log(
          '[useCartPricing] Skipping API call - another call is already in progress'
        );
        return;
      }

      // Mark that an API call is in progress
      isApiCallInProgressRef.current = true;

      // Increment request ID to mark this as the current request
      currentRequestIdRef.current += 1;
      const requestId = currentRequestIdRef.current;

      try {
        setIsCalculatingPrice(true);
        // Only set cartLoading to true if we don't have existing data (initial load)
        if (!hasInitialDataRef.current) {
          setCartLoading(true);
        }

        // Use cached token if still valid, otherwise fetch new one
        const now = Date.now();
        let token = authTokenCacheRef.current;
        if (!token || now - tokenTimestampRef.current > TOKEN_CACHE_DURATION) {
          token = await auth?.currentUser?.getIdToken();
          authTokenCacheRef.current = token;
          tokenTimestampRef.current = now;
        }

        // Check if this request is still current (not superseded by a newer one)
        if (requestId !== currentRequestIdRef.current) {
          return;
        }

        const headers = {
          'Content-Type': 'application/json',
          Authorization: token,
        };

        const deliveryPickupType = currentType || orderType || 'pickup';
        const deliveryId =
          deliveryPickupType === 'delivery'
            ? explicitDeliveryId || getCurrentDeliveryAddressId()
            : '';

        const requestBody = {
          storeId: cartStoreId || '',
          deliveryOrPickup: deliveryPickupType || 'pickup',
          restaurantId: environment.restaurantId,
          tipAmount: 0,
          uid: auth?.currentUser?.uid,
          deliveryId,
        };

        const response = await fetchData(
          `${environment?.apiUrl}/calculateSquareItemsPrice?restaurantId=${environment.restaurantId}`,
          'POST',
          requestBody,
          headers
        );

        // Check if this request is still current before updating state
        if (requestId !== currentRequestIdRef.current) {
          return;
        }

        if (response) {
          // Update both pricing data and loading state together
          const newPricingData = {
            serviceChargeMoney:
              response.serviceChargeMoney ||
              response.data?.serviceChargeMoney ||
              '0',
            subtotal: response.subtotal || response.data?.subtotal || 0,
            tax: response.tax || response.data?.tax || '0',
            tip: response.tip || response.data?.tip || '0',
            total: response.total || response.data?.total || 0,
            total_items:
              response.total_items || response.data?.total_items || 0,
            total_modifiers:
              response.total_modifiers || response.data?.total_modifiers || 0,
            total_modifiers_price:
              response.total_modifiers_price ||
              response.data?.total_modifiers_price ||
              0,
            total_modifiers_price_percentage:
              response.total_modifiers_price_percentage ||
              response.data?.total_modifiers_price_percentage ||
              0,
            total_discounts:
              response.total_discounts || response.data?.total_discounts || 0,
            lineItemData:
              response.lineItemsPrice || response.data?.lineItemsPrice || null,
            taxes_text:
              response.taxes_text || response.data?.taxes_text || null,
            onlineFee: response.onlineFee || response.data?.onlineFee || null,
          };

          // Use flushSync to force immediate DOM update when data arrives
          flushSync(() => {
            setApiPricingData(newPricingData);
            setCartLoading(false);
            setIsCalculatingPrice(false);
            setPricingError(null);
          });
          hasInitialDataRef.current = true; // Mark that we have initial data
        } else {
          // No response data, just clear loading state
          flushSync(() => {
            setCartLoading(false);
            setIsCalculatingPrice(false);
            setPricingError(
              new Error('No pricing data returned from server.')
            );
          });
        }
      } catch (_apiError) {
        // Only update state if this is still the current request
        if (requestId === currentRequestIdRef.current) {
          flushSync(() => {
            setCartLoading(false);
            setIsCalculatingPrice(false);
            setPricingError(_apiError || new Error('Failed to load pricing.'));
          });
        }
      } finally {
        // Always clear the in-progress flag to allow future API calls
        isApiCallInProgressRef.current = false;
      }
    },
    [enabled, cartStoreId, orderType, getCurrentDeliveryAddressId]
  );

  // Debounced API call function to prevent rapid fire requests
  // Uses adaptive delay: longer delay for rapid changes (quantity updates),
  // shorter for type changes.
  const debouncedApiCall = useCallback(
    (currentType = null, delay = 500) => {
      if (!enabled) return;

      // Clear previous timeout
      if (apiCallTimeoutRef.current) {
        clearTimeout(apiCallTimeoutRef.current);
      }

      // Set new timeout with adaptive delay
      const adaptiveDelay = currentType !== null ? 300 : delay;

      apiCallTimeoutRef.current = setTimeout(() => {
        callCalculateSquareItemsPriceImmediate(currentType);
      }, adaptiveDelay);
    },
    [enabled, callCalculateSquareItemsPriceImmediate]
  );

  // Public API call function (with debouncing by default)
  const callCalculateSquareItemsPrice = useCallback(
    (currentType = null, immediate = false) => {
      if (immediate) {
        return callCalculateSquareItemsPriceImmediate(currentType);
      } else {
        return debouncedApiCall(currentType);
      }
    },
    [callCalculateSquareItemsPriceImmediate, debouncedApiCall]
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (apiCallTimeoutRef.current) {
        clearTimeout(apiCallTimeoutRef.current);
      }
    };
  }, []);

  // React to cart changes and trigger pricing API when appropriate
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Use JSON.stringify for reliable deep comparison
    // This ensures we detect changes even if the array reference is the same
    const cartString = JSON.stringify(cart);

    // Skip if cart hasn't changed (deep comparison)
    // This prevents duplicate API calls when only 'enabled' changes but cart is the same
    if (lastCartStringRef.current === cartString) {
      return;
    }

    // Store the new cart string BEFORE processing
    const previousCartString = lastCartStringRef.current;
    lastCartStringRef.current = cartString;

    // Clear API data when cart is empty
    if (!cart || cart.length === 0) {
      setApiPricingData(null);
      setCartLoading(false);
      setPricingError(null);
      hasInitialDataRef.current = false; // Reset flag when cart is empty
      return;
    }

    // Always trigger API call when cart changes (throttling is handled in debouncedApiCall)
    // For first cart load, use shorter delay; for subsequent changes, use normal delay
    const now = Date.now();
    const isFirstCart = previousCartString === null;
    const timeSinceLastCall = now - lastApiCallRef.current;

    if (cart.length > 0 && auth?.currentUser?.uid) {
      // If first cart or enough time has passed since last call, trigger immediately
      if (isFirstCart || timeSinceLastCall > API_CALL_THROTTLE) {
        lastApiCallRef.current = now;
        // For first cart, use shorter delay; for changes, use normal delay
        debouncedApiCall(null, isFirstCart ? 100 : 500);
      } else {
        // Still trigger but with longer delay to respect throttle
        debouncedApiCall(null, API_CALL_THROTTLE - timeSinceLastCall + 100);
      }
    }
  }, [cart, enabled, debouncedApiCall]);

  return {
    apiPricingData,
    isCalculatingPrice,
    cartLoading,
    pricingError,
    // Expose helpers for components that need manual refresh
    refreshPricing: callCalculateSquareItemsPrice,
    callCalculateSquareItemsPrice,
    callCalculateSquareItemsPriceImmediate,
    getCurrentDeliveryAddressId,
  };
};

export default useCartPricing;
